'use client'

import { useState } from 'react'
import { useFormikContext } from 'formik'
import { RefreshCw } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { FormInput } from '@/components/form-inputs/form-input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { BillingPlan } from '@/lib/billing/types'
import { parseAmount, type PlanEditorValues } from './model'
import { FIELD_LABELS, NUMERIC_FIELDS } from './catalog'
import { formatPlanTimestamp } from './change-format'
import { FieldGrid, Notice } from './fields'

interface ProviderSettingsProps {
  planId?: string
  onSyncingChange: (syncing: boolean) => void
  onSynced: (plan: BillingPlan) => void
}

export function ProviderSettings({ planId, onSyncingChange, onSynced }: ProviderSettingsProps) {
  const { values, dirty, isSubmitting } = useFormikContext<PlanEditorValues>()
  const { fetchWithAuth } = useAuth()
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const price = parseAmount(values.price_monthly_cents, NUMERIC_FIELDS.price)
  const dashboardUrl = process.env.NEXT_PUBLIC_STRIPE_TEST_MODE === 'true'
    ? 'https://dashboard.stripe.com/test'
    : 'https://dashboard.stripe.com'

  async function sync() {
    if (!planId || dirty || isSubmitting || syncing) return
    setSyncing(true)
    onSyncingChange(true)
    setError(null)
    try {
      const response = await fetchWithAuth(`/api/admin/billing/plans/${planId}/stripe-sync`, { method: 'POST' })
      const data = await response.json()
      if (!response.ok || !data.plan) throw new Error(data.error ?? 'Stripe sync failed')
      onSynced(data.plan)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Stripe sync failed')
    } finally {
      setSyncing(false)
      onSyncingChange(false)
    }
  }

  if (values.billing_channel === 'shopify') {
    return (
      <div className="space-y-4">
        {/* One element: switching channels reuses this <p> (browser translation). */}
        <Notice onTint><span>
          <strong>Shopify plans do not sync automatically.</strong> Create and update the matching App Pricing plan in Shopify Partner Dashboard.
          After changing the name, price or billing interval here, update the matching plan there too.
        </span></Notice>
        <FormInput
          name="shopify_plan_handle"
          value={values.shopify_plan_handle ?? ''}
          label={FIELD_LABELS.shopify_plan_handle}
          placeholder="growth-monthly"
          description="Handle of the matching App Pricing plan in Shopify Partner Dashboard. Required before customers can subscribe through Shopify's plan-selection page."
        />
      </div>
    )
  }

  return <div className="space-y-4">
    <Notice onTint>{stripeSetupHint(planId, price, values.status)}</Notice>
    <FieldGrid>
      <FormInput name="stripe_product_id" value={values.stripe_product_id ?? ''} label="Stripe product ID" readOnly description="Created and maintained by Stripe sync." />
      <FormInput name="stripe_price_id" value={values.stripe_price_id ?? ''} label="Stripe price ID" readOnly description="Current Stripe price linked to this plan." />
    </FieldGrid>
    <div className="flex flex-wrap gap-4 text-sm">
      {values.stripe_product_id ? <a className="underline" href={`${dashboardUrl}/products/${encodeURIComponent(values.stripe_product_id)}`} target="_blank" rel="noopener noreferrer">Open Stripe product</a> : null}
      {values.stripe_price_id ? <a className="underline" href={`${dashboardUrl}/prices/${encodeURIComponent(values.stripe_price_id)}`} target="_blank" rel="noopener noreferrer">Open Stripe price</a> : null}
    </div>
    <div className="flex flex-wrap items-center gap-2 text-sm" role="status">
      <span>Sync status:</span>
      <StripeSyncBadge status={values.stripe_sync_status} />
    </div>
    <p className="text-xs text-muted-foreground">{`Last sync: ${formatSyncTime(values.stripe_synced_at)}`}</p>
    {values.stripe_sync_error ? <p role="alert" className="text-sm text-destructive">{values.stripe_sync_error}</p> : null}
    {error ? <p role="alert" className="text-sm text-destructive">{error}</p> : null}
    {planId && price !== null && price > 0 && values.status !== 'archived' ? <>
      <Button type="button" variant="outline" disabled={dirty || isSubmitting || syncing} onClick={sync}>
        <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
        <span>{syncing ? 'Syncing…' : syncButtonLabel(values.stripe_sync_status)}</span>
      </Button>
      {dirty ? <p className="text-xs text-muted-foreground">Save your changes before syncing.</p> : null}
    </> : null}
  </div>
}

function stripeSetupHint(planId: string | undefined, price: number | null, status: string): string {
  if (price === 0) return 'Free plans do not need a Stripe product or price. No Stripe sync is required.'
  if (status === 'archived') return 'Restore this plan from the plan list before syncing it to Stripe.'
  if (!planId) return 'Save this paid plan to create its Stripe product and price when automatic sync is enabled.'
  return 'Paid Stripe plans sync on save when automatic sync is enabled. You can also sync or retry here after saving your changes.'
}

function StripeSyncBadge({ status }: { status: PlanEditorValues['stripe_sync_status'] }) {
  switch (status) {
    case 'synced': return <Badge variant="secondary" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">Synced</Badge>
    case 'failed': return <Badge variant="destructive">Failed</Badge>
    case 'pending': return <Badge variant="secondary" className="bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300">Pending</Badge>
    case null: return <Badge variant="outline">Not synced</Badge>
  }
}

function formatSyncTime(value: string | null): string {
  if (!value) return 'Never'
  if (Number.isNaN(Date.parse(value))) return 'Unknown'
  return formatPlanTimestamp(value)
}

function syncButtonLabel(status: PlanEditorValues['stripe_sync_status']): string {
  return status === 'failed' ? 'Retry Stripe sync' : 'Sync to Stripe'
}
